import { IAppService } from '@/modules/app/service/IAppService';
import { ICanvasService } from '@/modules/canvas/services/Canvas/ICanvasService';
import { useAppStore } from '@/modules/app/store/useAppStore';

import { AreaPickerStartOptions, IAreaPickerService } from './IAreaPickerService';

type PendingPick = {
  token: symbol;
  onPick: (areaId: string) => void;
  onCancel?: () => void;
  previousSelectedIds: string[];
};

export class AreaPickerServiceImpl implements IAreaPickerService {
  private appSvc: IAppService;
  private canvasSvc: ICanvasService;
  private pending: PendingPick | null = null;

  public constructor(appSvc: IAppService, canvasSvc: ICanvasService) {
    this.appSvc = appSvc;
    this.canvasSvc = canvasSvc;
  }

  public start({ enabledAreaIds, onPick, onCancel, message }: AreaPickerStartOptions): symbol {
    const token = Symbol('area-pick');
    const previousSelectedIds = [...useAppStore.getState().selectedIds];
    this.pending = { token, onPick, onCancel, previousSelectedIds };
    this.appSvc.activateTool('choose');
    this.canvasSvc.enableTargeting(enabledAreaIds);
    if (message) {
      this.appSvc.setCanvasOverlay({
        message,
        onCancel: () => this.abort(),
      });
    }
    return token;
  }

  public complete(areaId: string): void {
    this.pending?.onPick(areaId);
  }

  public refresh(enabledAreaIds: string[]): void {
    if (this.pending === null) return;
    this.canvasSvc.enableTargeting(enabledAreaIds);
  }

  public cancel(token: symbol): void {
    if (this.pending?.token === token) {
      this.endSession();
    }
  }

  public abort(): void {
    if (this.pending !== null) {
      this.endSession();
    }
  }

  public isActive(): boolean {
    return this.pending !== null;
  }

  private endSession(): void {
    const { pending } = this;
    this.pending = null;
    this.appSvc.setCanvasOverlay(null);
    this.canvasSvc.disableTargeting();
    this.appSvc.resetTool();
    if (pending) {
      this.appSvc.setSelectedIds(pending.previousSelectedIds);
      this.canvasSvc.setSelection(pending.previousSelectedIds);
      pending.onCancel?.();
    }
  }
}
